<template>
  <div class="custom-node node-question" :class="{ collapsed: collapsed }">
    <Handle type="target" :position="Position.Left" id="input" />

    <!-- Header avec bouton collapse -->
    <div class="node-header">
      <div class="node-header-content">
        <div class="node-icon">
          <i class="fas fa-question-circle"></i>
        </div>
        <div class="node-title">
          {{ label || 'Question' }}
        </div>
      </div>
      <button
        @click.stop="toggleCollapse"
        class="collapse-btn"
        :title="collapsed ? 'Déplier' : 'Replier'"
      >
        <i :class="collapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
      </button>
    </div>

    <!-- Contenu (masqué si collapsed) -->
    <div v-if="!collapsed" class="node-content">
      <div v-if="selectedElement" class="node-element-preview">
        <p class="question-text">{{ truncate(selectedElement.el_Text, 60) }}</p>

        <!-- Options de réponse -->
        <div v-if="selectedElement.options" class="options-list">
          <div
            v-for="option in selectedElement.options"
            :key="option.id"
            class="option-item"
            :class="{ correct: option.isCorrect }"
          >
            <span class="option-letter">{{ getOptionLetter(option.id) }}</span>
            <span class="option-text">{{ truncate(option.text, 30) }}</span>
          </div>
        </div>
      </div>
      <div v-else class="node-element-preview text-muted">
        <em>Aucune question sélectionnée</em>
      </div>
    </div>

    <!-- Footer avec statistiques -->
    <div class="node-footer">
      <small class="text-muted">
        <i class="fas fa-link me-1"></i>
        {{ outgoingEdgesCount }} sortie(s)
      </small>
    </div>

    <!-- Handles pour les options de réponse -->
    <div class="option-handles">
      <Handle
        v-for="option in visibleOptions"
        :key="`source-${option.id}`"
        :id="option.id"
        type="source"
        :position="Position.Right"
        :style="{ top: getHandlePosition(option.id) }"
        class="option-handle"
      >
        <span class="handle-label">{{ getOptionLetter(option.id) }}</span>
      </Handle>
    </div>

    <!-- Bouton suppression -->
    <button
      class="node-delete-btn"
      @click.stop="handleDelete"
      title="Supprimer le nœud"
    >
      <i class="fas fa-times"></i>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Handle, Position, useVueFlow } from '@vue-flow/core'

const props = defineProps({
  id: String,
  data: Object,
  label: String,
  collapsed: Boolean,
  availableElements: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['delete', 'update'])

const { getEdges } = useVueFlow()

const selectedElement = computed(() => {
  if (!props.data.elementId) return null
  return props.availableElements.find(el => el.el_ID === props.data.elementId)
})

const outgoingEdgesCount = computed(() => {
  const edges = getEdges.value
  return edges.filter(e => e.source === props.id).length
})

// Options visibles selon l'état collapsed
const visibleOptions = computed(() => {
  if (!selectedElement.value?.options) return []

  if (props.collapsed) {
    // En mode replié, afficher uniquement les options qui ont des connexions
    const edges = getEdges.value
    const connectedOptionIds = edges
      .filter(e => e.source === props.id)
      .map(e => e.sourceHandle)
      .filter(Boolean)

    return selectedElement.value.options.filter(opt =>
      connectedOptionIds.includes(opt.id)
    )
  }

  // En mode déplié, afficher toutes les options
  return selectedElement.value.options
})

function truncate(text, maxLength) {
  if (!text) return ''
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

function getOptionLetter(optionId) {
  // Extrait la lettre de l'ID (ex: "QC123_choice1" -> "A")
  if (!selectedElement.value?.options) return ''
  const index = selectedElement.value.options.findIndex(opt => opt.id === optionId)
  return String.fromCharCode(65 + index) // 65 = 'A'
}

function getHandlePosition(optionId) {
  if (!selectedElement.value?.options) return '0px'

  if (props.collapsed) {
    // Position pour les handles en mode collapsed
    const visibleIndex = visibleOptions.value.findIndex(opt => opt.id === optionId)
    const baseOffset = 25
    const spacing = 15
    return `${baseOffset + (visibleIndex * spacing)}px`
  } else {
    // Position pour les handles en mode étendu
    const optionIndex = selectedElement.value.options.findIndex(opt => opt.id === optionId)
    const headerHeight = 45 // Hauteur du header
    const contentPadding = 15
    const questionTextHeight = 30 // Approximation
    const baseOffset = headerHeight + contentPadding + questionTextHeight + 15
    const spacing = 35 // Espacement entre chaque option
    return `${baseOffset + (optionIndex * spacing)}px`
  }
}

function toggleCollapse() {
  emit('update', props.id, { collapsed: !props.collapsed })
}

function handleDelete() {
  if (confirm('Supprimer ce nœud question ?')) {
    emit('delete', props.id)
  }
}
</script>

<style scoped>
.custom-node {
  position: relative;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 220px;
  max-width: 350px;
  background: white;
  border: 2px solid;
  transition: all 0.3s ease;
}

.custom-node.collapsed {
  min-width: 180px;
  max-width: 220px;
}

.custom-node:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.node-question {
  border-color: #007bff;
  background: white;
}

/* Header */
.node-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  border-radius: 6px 6px 0 0;
}

.custom-node.collapsed .node-header {
  border-radius: 6px;
  border-bottom: none;
}

.node-header-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.node-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  flex-shrink: 0;
}

.node-title {
  font-weight: 600;
  font-size: 13px;
  color: #212529;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.collapse-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  padding: 4px 8px;
  border-radius: 3px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.collapse-btn:hover {
  background-color: #dee2e6;
  color: #333;
}

/* Content */
.node-content {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

.question-text {
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  margin: 0 0 10px 0;
  line-height: 1.4;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #dee2e6;
  font-size: 11px;
}

.option-item.correct {
  border-left-color: #28a745;
  background: #d4edda;
}

.option-letter {
  font-weight: bold;
  color: #007bff;
  min-width: 16px;
  font-size: 11px;
}

.option-text {
  flex: 1;
  color: #495057;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.node-element-preview.text-muted {
  font-style: italic;
  color: #6c757d;
  font-size: 12px;
}

/* Footer */
.node-footer {
  padding: 6px 12px;
  background: #f8f9fa;
  border-radius: 0 0 6px 6px;
  font-size: 11px;
}

.custom-node.collapsed .node-footer {
  border-radius: 0 0 6px 6px;
}

/* Option Handles */
.option-handles {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  pointer-events: none;
}

.option-handle {
  background-color: #007bff !important;
  border: 2px solid white !important;
  width: 14px !important;
  height: 14px !important;
  transform: translateX(50%);
  pointer-events: all;
  transition: all 0.2s ease;
}

.option-handle:hover {
  background-color: #0056b3 !important;
  transform: translateX(50%) scale(1.2);
}

.handle-label {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  background-color: #007bff;
  color: white;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: bold;
  pointer-events: none;
  white-space: nowrap;
}

/* Delete Button */
.node-delete-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #dc3545;
  color: white;
  border: 2px solid white;
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.custom-node:hover .node-delete-btn {
  display: flex;
}

.node-delete-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}
</style>
